import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ChefHat } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function LoginPage() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const [authenticating, setAuthenticating] = useState(false);
  const [authError, setAuthError] = useState('');
  const [googleReady, setGoogleReady] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleGoogleCredential = useCallback(async (googleResponse) => {
    if (!googleResponse?.credential) {
      setAuthError('Falha ao receber a credencial do Google.');
      return;
    }

    setAuthenticating(true);
    setAuthError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ credential: googleResponse.credential }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Falha na autenticacao com Google.');
      }

      const data = await response.json();
      setUser(data.user);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Google login failed:', error);
      setAuthError(error.message || 'Nao foi possivel concluir o login com Google.');
    } finally {
      setAuthenticating(false);
    }
  }, [navigate, setUser]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      setAuthError('Google OAuth nao configurado.');
      return;
    }

    let isCancelled = false;
    let script;

    const initializeGoogle = () => {
      if (isCancelled || !buttonRef.current || !window.google?.accounts?.id) {
        return;
      }

      buttonRef.current.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
        auto_select: false,
        cancel_on_tap_outside: true,
        ux_mode: 'popup',
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: 'filled_black',
        size: 'large',
        shape: 'pill',
        text: 'continue_with',
        width: 320,
        logo_alignment: 'left',
      });
      setGoogleReady(true);
    };

    if (window.google?.accounts?.id) {
      initializeGoogle();
      return () => {
        isCancelled = true;
      };
    }

    script = document.querySelector('script[data-google-identity="true"]');
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.dataset.googleIdentity = 'true';
      document.head.appendChild(script);
    }

    script.addEventListener('load', initializeGoogle);

    return () => {
      isCancelled = true;
      script?.removeEventListener('load', initializeGoogle);
    };
  }, [handleGoogleCredential]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1769955821416-9273532bb148?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb21tZXJjaWFsJTIwa2l0Y2hlbiUyMGNoZWYlMjB3b3JraW5nJTIwZGFyayUyMGFlc3RoZXRpY3N8ZW58MHx8fHwxNzczODM3OTAxfDA&ixlib=rb-4.1.0&q=85')`
        }}
      />
      <div className="absolute inset-0 bg-zinc-950/80 backdrop-blur-sm" />

      {/* Login Card */}
      <Card className="relative w-full max-w-md glass border-zinc-800 animate-fade-in" data-testid="login-card">
        <CardHeader className="text-center space-y-4 pb-2">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-white flex items-center justify-center">
            <ChefHat className="w-9 h-9 text-zinc-900" />
          </div>
          <div>
            <CardTitle className="text-3xl font-bold text-zinc-50 tracking-tight">
              Kitchen Scale Manager
            </CardTitle>
            <CardDescription className="text-zinc-400 mt-2">
              Sistema de gerenciamento de escalas para cozinha institucional
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="pt-6 pb-8">
          <div className="space-y-6">
            <div className="space-y-2 text-center">
              <p className="text-sm text-zinc-500">
                Faça login para acessar suas escalas e designações
              </p>
            </div>
            <div className="flex justify-center" data-testid="google-login-btn">
              {authenticating ? (
                <div className="w-full h-12 rounded-full bg-zinc-900 border border-zinc-800 text-zinc-100 flex items-center justify-center gap-3">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm font-medium">Autenticando com Google...</span>
                </div>
              ) : (
                <div ref={buttonRef} className="min-h-[44px]" />
              )}
            </div>
            {!googleReady && !authenticating && !authError && (
              <p className="text-xs text-zinc-500 text-center">
                Carregando login Google...
              </p>
            )}
            {authError && (
              <p className="text-sm text-red-400 text-center">
                {authError}
              </p>
            )}
            <p className="text-xs text-zinc-600 text-center">
              Ao fazer login, você concorda com os termos de uso do sistema
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
